import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Menu, User as UserIcon, LogOut, Wallet, CreditCard, ReceiptText, ShieldCheck, Bell, Settings, FileText, ChevronRight, Eye, EyeOff, KeyRound, RefreshCw, Loader2, Search, X } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { mockSportsMenu } from './mock/mockData';
import { API_ENDPOINTS } from '../config/api';
import { useToast } from '../hooks/use-toast';
import BalanceService from '../services/BalanceService';
import LoginModal from './LoginModal';

const HEADER_NAV_PATH_MAP = {
  'Home': '/',
  'Cricket': '/cricket',
  '🏆 IPL 2026': '/ipl-2026',
  'Casino': '/casino',
  'Football': '/soccer',
  'Tennis': '/tennis',
  'Sports Book': '/sportsbook',
  'Horse Racing': '/horse-racing',
  'Greyhound Racing': '/greyhound-racing',
  'Binary': '/binary',
  'Kabaddi': '/kabaddi',
  'Politics': '/election',
  'Basketball': '/basketball',
  'Baseball': '/baseball',
  'Table Tennis': '/table-tennis',
  'Volleyball': '/volleyball',
  'Ice Hockey': '/ice-hockey',
  'Rugby': '/rugby',
  'Mixed Martial Arts': '/mma',
  'Darts': '/darts',
  'Futsal': '/futsal'
};

const badgeMap = {
  silver: { icon: '🥈', color: '#64748b', label: 'Silver' },
  gold: { icon: '🥇', color: '#f59e0b', label: 'Gold' },
  platinum: { icon: '💎', color: '#7c3aed', label: 'Platinum' },
};

const BadgePill = ({ badge, size = 'sm' }) => {
  if (!badge) return null;
  const cfg = badgeMap[String(badge).toLowerCase()];
  if (!cfg) return null;
  const px = size === 'lg' ? '4px 10px' : '2px 8px';
  const fs = size === 'lg' ? '12px' : '10px';
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full font-bold whitespace-nowrap"
      style={{ background: cfg.color, color: '#fff', padding: px, fontSize: fs, lineHeight: 1 }}
      title={`${cfg.label} member`}
    >
      <span>{cfg.icon}</span>
      <span>{cfg.label}</span>
    </span>
  );
};

const fmtExposure = (val) => {
  const n = Math.abs(Number(val) || 0);
  if (n === 0) return '₹0';
  return `-₹${Math.round(n).toLocaleString('en-IN')}`;
};

const Header = ({ user, setUser, onMenuClick, onNavigate }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [ticker, setTicker] = useState(null);
  useEffect(() => {
    fetch('/api/ticker')
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && d.success && d.enabled && d.text) setTicker(d.text); })
      .catch(() => {});
  }, []);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [searchMatches, setSearchMatches] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [allMatchesCache, setAllMatchesCache] = useState([]);
  const searchBoxRef = useRef(null);
  const { toast } = useToast();
  const [showLogin, setShowLogin] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [loginData, setLoginData] = useState({ username: '', phone_number: '', password: '' });
  const [loginType, setLoginType] = useState('username'); // 'username' or 'phone'
  const [signupData, setSignupData] = useState({ phoneNumber: '', password: '', confirmPassword: '' });
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showSignupConfirmPassword, setShowSignupConfirmPassword] = useState(false);
  
  // Dynamic balance state — initialize from localStorage
  const [balance, setBalance] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem('reddyannabook_user') || '{}');
      return {
        main_balance: Number(s.balance ?? 0),
        available_credit: Number(s.available_credit ?? s.balance ?? 0),
        bonus_balance: Number(s.bonus_balance ?? 0),
        net_exposure: Number(s.net_exposure ?? 0),
        exposure: Number(s.net_exposure ?? 0),
      };
    } catch { return null; }
  });
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceError, setBalanceError] = useState('');
  const [bestCaseWinnings, setBestCaseWinnings] = useState(0);
  const [notifUnread, setNotifUnread] = useState(0);
  const [notifList, setNotifList] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef(null);

  // Form loading states
  const [loginLoading, setLoginLoading] = useState(false);
  const [signupLoading, setSignupLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  // Prevent multiple balance fetches
  const balanceFetchRef = useRef(false);
  
  // Sheet state for controlling open/close
  const [sheetOpen, setSheetOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef(null);
  
  // Close account dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(e.target)) setAccountMenuOpen(false);
    };
    if (accountMenuOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [accountMenuOpen]);

  // Notification bell: fetch + poll + outside click
  useEffect(() => {
    const fetchNotifs = async () => {
      try {
        const token = localStorage.getItem('reddyannabook_token');
        if (!token || localStorage.getItem('reddyannabook_demo_mode') === 'true') return;
        const res = await fetch('/api/user/notifications', { headers: { 'Authorization': `Bearer ${token}` } });
        const d = await res.json();
        if (d.success) { setNotifUnread(d.data?.unread ?? 0); setNotifList(d.data?.notifications ?? []); }
      } catch {}
    };
    fetchNotifs();
    const iv = setInterval(fetchNotifs, 30000);
    return () => clearInterval(iv);
  }, []);
  useEffect(() => {
    const h = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false); };
    if (notifOpen) document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [notifOpen]);
  const markNotifsRead = async () => {
    try {
      const token = localStorage.getItem('reddyannabook_token');
      await fetch('/api/user/notifications/read', { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
      setNotifUnread(0);
      setNotifList(prev => prev.map(n => ({ ...n, is_read: 1 })));
    } catch {}
  };

  // Search: fetch matches + filter + outside click
  useEffect(() => {
    (async () => {
      try {
        const CricketAPIService = (await import('../services/CricketAPIService')).default;
        const data = await CricketAPIService.getAllMatches();
        if (data.success) setAllMatchesCache(data.matches || []);
      } catch {}
    })();
  }, []);
  useEffect(() => {
    if (searchQuery.length < 2) { setSearchMatches([]); setShowSearchResults(false); return; }
    const q = searchQuery.toLowerCase();
    setSearchMatches(allMatchesCache.filter(m => (m.name||'').toLowerCase().includes(q) || (m.home_team||'').toLowerCase().includes(q) || (m.away_team||'').toLowerCase().includes(q) || (m.competition||'').toLowerCase().includes(q)).slice(0, 8));
    setShowSearchResults(true);
  }, [searchQuery, allMatchesCache]);
  useEffect(() => {
    const h = (e) => { if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) setShowSearchResults(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Handle navigation with sheet close
  const handleNavigate = () => {
    setSheetOpen(false);
    if (onNavigate) onNavigate();
  };

  // Reset password form
  const resetPasswordForm = () => {
    setPwd({ current: '', next: '', confirm: '' });
    setPwdErrors({});
    setShow1(false); setShow2(false); setShow3(false);
    setCpStep(1); setCpOtp(''); setCpCountdown(0);
    if (cpTimerRef.current) clearInterval(cpTimerRef.current);
  };
  

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    
    // Prepare login data based on login type
    const loginPayload = {
      password: loginData.password,
    };

    if (loginType === 'username') {
      loginPayload.username = loginData.username;
    } else {
      loginPayload.phone_number = loginData.phone_number;
    }
    
    try {
      const response = await fetch(API_ENDPOINTS.LOGIN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginPayload),
      });

      let data;
      try {
        data = await response.json();
      } catch (_) {
        alert('Invalid response from server. Please try again.');
        setLoginLoading(false);
        return;
      }

      if (response.ok) {
        const user = data.data?.user ?? data.user;
        const token = data.data?.token ?? data.token;
        if (user) {
          // Set must_change_password in localStorage BEFORE setUser triggers re-render
          const mustChange = data.data?.must_change_password ?? data.must_change_password;
          if (mustChange) {
            localStorage.setItem('must_change_password', 'true');
          } else {
            localStorage.removeItem('must_change_password');
          }
          try {
            localStorage.setItem('reddyannabook_user', JSON.stringify(user));
            if (token) localStorage.setItem('reddyannabook_token', token);
          } catch (_) {}
          setUser(user);
          setShowLogin(false);
          setLoginData({ username: '', phone_number: '', password: '' });
          if (mustChange) {
            window.location.href = '/force-change-password';
          } else {
            window.location.href = '/';
          }
        } else {
          alert('Login succeeded but no user data received.');
        }
      } else {
        alert(data.message || data.error || 'Login failed');
      }
    } catch (error) {
      alert('Network error. Please check your connection and try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    
    // Frontend validation
    if (signupData.password !== signupData.confirmPassword) {
      alert('Passwords do not match');
      return;
    }
    
    setSignupLoading(true);

    const phoneNumber = String(signupData.phoneNumber || '').replace(/\D/g, '');

    try {
      const response = await fetch(API_ENDPOINTS.REGISTER, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: phoneNumber || signupData.phoneNumber,
          phone_number: phoneNumber || signupData.phoneNumber,
          password: signupData.password,
          display_name: signupData.phoneNumber || phoneNumber,
        }),
      });

      let data;
      try {
        data = await response.json();
      } catch (_) {
        alert('Invalid response from server. Please try again.');
        setSignupLoading(false);
        return;
      }

      if (response.ok) {
        const user = data.data?.user ?? data.user;
        const token = data.data?.token ?? data.token;
        if (user) {
          try {
            localStorage.setItem('reddyannabook_user', JSON.stringify(user));
            if (token) localStorage.setItem('reddyannabook_token', token);
          } catch (_) {}
          setUser(user);
          setShowSignup(false);
          setSignupData({ phoneNumber: '', password: '', confirmPassword: '' });
          const mustChangeReg = data.data?.must_change_password ?? data.must_change_password;
          if (mustChangeReg) {
            localStorage.setItem('must_change_password', 'true');
            window.location.href = '/force-change-password';
          } else {
            localStorage.removeItem('must_change_password');
            window.location.href = '/';
          }
        } else {
          alert('Registration succeeded but no user data received.');
        }
      } else {
        alert(data.message || data.error || 'Registration failed');
      }
    } catch (error) {
      alert('Network error. Please check your connection and try again.');
    } finally {
      setSignupLoading(false);
    }
  };

  // Fetch best case winnings from open bets
  const fetchBestCaseWinnings = async () => {
    try {
      const token = localStorage.getItem('reddyannabook_token');
      if (!token) return;
      const apiBase = API_ENDPOINTS.BETS_PLACE?.replace('/bets/place', '') || '/api';
      const res = await fetch(`${apiBase}/bets/open`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!res.ok) return;
      const bets = data.data?.open_bets || data.data?.bets || data.open_bets || (Array.isArray(data.data) ? data.data : []);
      if (!bets || !bets.length) { setBestCaseWinnings(0); return; }

      const FANCY_TYPES = ['fancy', 'session', 'yes_no'];
      const INDEP_TYPES = ['premium', 'innings_runs', 'over_runs', 'player_runs', 'player_wickets'];
      const isFT = (mt) => FANCY_TYPES.includes((mt || '').toLowerCase().trim());

      const markets = {};
      bets.forEach(b => {
        const gid = b.game_id || '';
        const mtype = (b.market_type || 'match_odds').toLowerCase().trim();
        const mid = b.market_id || '';
        let key;
        if (isFT(mtype)) key = 'f_' + (mid || gid + '_f_' + b.id);
        else if (INDEP_TYPES.includes(mtype)) key = 'i_' + (mid || gid + '_' + mtype);
        else key = 's_' + gid + '_' + (mtype || 'match_odds');
        if (!markets[key]) markets[key] = [];
        markets[key].push(b);
      });

      let total = 0;
      Object.values(markets).forEach(mb => {
        const mtype = (mb[0]?.market_type || '').toLowerCase();
        if (isFT(mtype)) {
          const lines = [...new Set(mb.map(b => parseFloat(b.fancy_line || b.line || 0)).filter(l => l > 0))].sort((a,b) => a-b);
          const pts = lines.length > 0
            ? [lines[0]-1, ...lines.slice(0,-1).map((l,i) => (l+lines[i+1])/2), lines[lines.length-1]+1]
            : [0];
          let best = -Infinity;
          pts.forEach(runs => {
            let pnl = 0;
            mb.forEach(b => {
              const st = parseFloat(b.stake) || 0;
              const line = parseFloat(b.fancy_line || b.line || 0);
              const side = (b.side || 'back').toLowerCase().includes('lay') ? 'lay' : 'back';
              if (side === 'back') pnl += runs > line ? st : -st;
              else pnl += runs < line ? st : -st;
            });
            if (pnl > best) best = pnl;
          });
          if (best > 0) total += best;
        } else {
          const sels = [...new Set(mb.map(b => b.selection))];
          let best = -Infinity;
          sels.forEach(winner => {
            let pnl = 0;
            mb.forEach(b => {
              const st = parseFloat(b.stake) || 0;
              const od = parseFloat(b.odds) || 0;
              const side = (b.side || 'back').toLowerCase().includes('lay') ? 'lay' : 'back';
              const isW = b.selection === winner;
              if (side === 'back') { pnl += isW ? Math.round(st * (od - 1)) : -st; }
              else { pnl += isW ? -Math.round(st * (od - 1)) : st; }
            });
            if (pnl > best) best = pnl;
          });
          if (sels.length !== 2) {
            let opPnl = 0;
            mb.forEach(b => { const side = (b.side||'back').toLowerCase().includes('lay') ? 'lay' : 'back'; if (side === 'back') opPnl -= parseFloat(b.stake)||0; else opPnl += parseFloat(b.stake)||0; });
            if (opPnl > best) best = opPnl;
          }
          if (best > 0) total += best;
        }
      });
      setBestCaseWinnings(Math.round(total));
    } catch (e) { /* silent */ }
  };


  // Fetch user balance from server
  const fetchUserBalance = async () => {
    if (!user) return;

    const token = localStorage.getItem('reddyannabook_token');
    if (!token) return; // Skip entirely if no token
    if (user.id === 'demo' || user.id === 0 || user.is_demo || isDemoMode) {
      setBalance({
        main_balance: user.balance ?? user.availableCredit ?? 0,
        bonus_balance: user.bonusWallet ?? user.bonus_balance ?? 0,
        available_credit: user.availableCredit ?? user.balance ?? 0,
        net_exposure: user.netExposure ?? user.net_exposure ?? 0,
        exposure: user.netExposure ?? user.net_exposure ?? 0,
      });
      return;
    }

    balanceFetchRef.current = true;
    try {
      const response = await fetch(API_ENDPOINTS.USER_BALANCE, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.status === 401) {
        setBalance(0);
        return;
      }

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) return;

      let data;
      try {
        const text = await response.text();
        if (!text || text.includes('Just a moment')) return;
        data = JSON.parse(text);
      } catch { return; }

      if (data?.data?.balance) {
        const b = data.data.balance;
        const exp = Number(b.exposure ?? b.net_exposure ?? 0);
        setBalance(prev => ({
          ...prev,
          main_balance: Number(b.main_balance ?? prev?.main_balance ?? 0),
          available_credit: Number(b.available_credit ?? prev?.available_credit ?? 0),
          bonus_balance: Number(b.bonus_balance ?? prev?.bonus_balance ?? 0),
          net_exposure: exp || prev?.net_exposure || 0,
          exposure: exp || prev?.exposure || 0,
        }));
        const updatedUser = {
          ...user,
          balance: Number(b.main_balance ?? 0),
          available_credit: Number(b.available_credit ?? 0),
          bonus_balance: Number(b.bonus_balance ?? 0),
          net_exposure: exp,
          badge: data?.data?.badge ?? data?.data?.user?.badge ?? user.badge,
          whatsapp_link: data?.data?.whatsapp_link ?? user.whatsapp_link,
          telegram_link: data?.data?.telegram_link ?? user.telegram_link,
          instagram_link: data?.data?.instagram_link ?? user.instagram_link,
        };
        setUser(updatedUser);
        localStorage.setItem('reddyannabook_user', JSON.stringify(updatedUser));
      }
    } catch (err) {
      // Network error — keep last known balance, log silently
      console.warn('Balance fetch failed (network):', err.message);
    } finally {
      balanceFetchRef.current = false;
    }
  };

  // Balance polling — wait for token, then poll every 30s
  useEffect(() => {
    if (!user) return;

    const startPolling = () => {
      const token = localStorage.getItem('reddyannabook_token');
      if (!token) return;
      fetchUserBalance();
      fetchBestCaseWinnings();
    };

    // Wait 500ms after mount/login to ensure token is saved
    const initTimer = setTimeout(startPolling, 500);

    // Poll every 30s
    const pollInterval = setInterval(startPolling, 30000);

    // Listen for manual balance updates + login event
    const handleManualUpdate = () => { startPolling(); };
    window.addEventListener('balance-update', handleManualUpdate);
    window.addEventListener('user-logged-in', handleManualUpdate);

    return () => {
      clearTimeout(initTimer);
      clearInterval(pollInterval);
      window.removeEventListener('balance-update', handleManualUpdate);
      window.removeEventListener('user-logged-in', handleManualUpdate);
    };
  }, [user?.id]);

  const handleLogout = () => {
    try {
      localStorage.removeItem('reddyannabook_user');
      localStorage.removeItem('reddyannabook_token');
      localStorage.removeItem('reddyannabook_demo_mode');
    } catch (_) {}
    setUser(null);
    setBalance(null);
  };

  const isDemoMode = typeof window !== 'undefined' && localStorage.getItem('reddyannabook_demo_mode') === 'true';

  const handleLoginSuccess = (userData, mustChange = false) => {
    localStorage.setItem('reddyannabook_user', JSON.stringify(userData));
    setUser(userData);
    setShowLogin(false);
    if (mustChange) {
      localStorage.setItem('must_change_password', 'true');
      window.location.href = '/force-change-password';
    } else {
      localStorage.removeItem('must_change_password');
      window.location.href = '/';
    }
  };

  // Change Password Modal state
  const [showChangePwd, setShowChangePwd] = useState(false);
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const [show1, setShow1] = useState(false);
  const [show2, setShow2] = useState(false);
  const [show3, setShow3] = useState(false);
  const [pwdErrors, setPwdErrors] = useState({});
  const [cpStep, setCpStep] = useState(1);
  const [cpOtp, setCpOtp] = useState('');
  const [cpCountdown, setCpCountdown] = useState(0);
  const cpTimerRef = useRef(null);

  const validatePasswordForm = () => {
    const errors = {};
    
    if (!pwd.current.trim()) {
      errors.current = 'Current password is required';
    }
    
    if (!pwd.next.trim()) {
      errors.next = 'New password is required';
    } else if (pwd.next.length < 6) {
      errors.next = 'New password must be at least 6 characters';
    }
    
    if (!pwd.confirm.trim()) {
      errors.confirm = 'Confirm password is required';
    } else if (pwd.next !== pwd.confirm) {
      errors.confirm = 'Passwords do not match';
    }
    
    setPwdErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Step 1: Send OTP
  const handleSendPwdOtp = async (e) => {
    if (e) e.preventDefault();
    if (!validatePasswordForm()) return;
    setPasswordLoading(true); setPwdErrors({});
    try {
      const token = localStorage.getItem('reddyannabook_token');
      if (!token) { toast({ title: 'Error', description: 'Please login', variant: 'destructive' }); return; }
      const res = await fetch('/api/auth/send-otp', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ phone: '0000000000', purpose: 'change_password' }) });
      const text = await res.text(); let data; try { data = JSON.parse(text); } catch { data = { success: false, message: 'Server error' }; }
      if (data.success) {
        setCpStep(2); setCpCountdown(60);
        if (cpTimerRef.current) clearInterval(cpTimerRef.current);
        cpTimerRef.current = setInterval(() => { setCpCountdown(p => { if (p <= 1) { clearInterval(cpTimerRef.current); return 0; } return p - 1; }); }, 1000);
        toast({ title: 'OTP Sent', description: 'Check your WhatsApp' });
      } else { setPwdErrors({ current: data.message || 'Failed to send OTP' }); }
    } catch (err) { setPwdErrors({ current: err.message || 'Network error' }); }
    finally { setPasswordLoading(false); }
  };

  // Step 2: Verify OTP + Change
  const handleVerifyPwdOtp = async () => {
    if (!cpOtp || cpOtp.length < 4) { setPwdErrors({ current: 'Enter OTP' }); return; }
    setPasswordLoading(true); setPwdErrors({});
    try {
      const token = localStorage.getItem('reddyannabook_token');
      const res = await fetch(API_ENDPOINTS.CHANGE_PASSWORD, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ current_password: pwd.current, new_password: pwd.next, otp: cpOtp }) });
      const text = await res.text(); let data; try { data = JSON.parse(text); } catch { data = { success: false, message: 'Server error' }; }
      if (data.success || res.ok) {
        toast({ title: 'Success', description: 'Password changed successfully' });
        setShowChangePwd(false); resetPasswordForm(); setCpStep(1); setCpOtp('');
        if (cpTimerRef.current) clearInterval(cpTimerRef.current);
      } else { setPwdErrors({ current: data.message || 'Failed' }); }
    } catch (err) { setPwdErrors({ current: err.message }); }
    finally { setPasswordLoading(false); }
  };

  // Legacy handler kept for form submit
  const handleSavePassword = async (e) => {
    e.preventDefault();
    handleSendPwdOtp(e);
  };

  return (
    <>
      {/* Demo Mode Banner */}
      {isDemoMode && (
        <div className="bg-yellow-400 text-yellow-900 text-center py-1.5 text-xs font-bold sticky top-0 z-[60]">
          Demo Mode — <button onClick={() => navigate('/register')} className="underline mx-1">Sign Up</button> to place bets |
          <button onClick={() => { localStorage.removeItem('reddyannabook_demo_mode'); navigate('/login'); }} className="underline ml-1">Exit</button>
        </div>
      )}
      {/* Main Header */}
      <header className={`z-50 w-full bg-lotus text-white/95 shadow-md border-b border-white/10`}>
        <div className="w-full max-w-[1920px] mx-auto px-3 sm:px-6">
          <div className="h-14 flex items-center gap-1.5 sm:gap-4">
            {/* Mobile: Back button (fixed width slot) */}
            {location.pathname !== '/' ? (
              <button aria-label="Go back" onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.history.back(); }} className="inline-flex items-center justify-center w-7 h-7 rounded-lg hover:bg-white/10 active:bg-white/20 sm:hidden shrink-0">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
              </button>
            ) : null}

            {/* Hamburger */}
            <button aria-label="Open menu" onClick={onMenuClick} className="inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-white/10 md:hidden shrink-0">
              <Menu className="w-[18px] h-[18px]" />
            </button>

            {/* Logo — always visible */}
            <Link to="/" className="shrink-0 flex items-center">
              <img src="/logo.png" alt="Reddy Anna Book" className="h-6 sm:h-9" style={{ objectFit: 'contain' }} />
            </Link>

            {/* Desktop Search with results dropdown */}
            <div ref={searchBoxRef} className="hidden sm:flex flex-1 justify-center mx-4">
              <div className="relative w-full max-w-[400px]">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search matches..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => searchQuery.length >= 2 && setShowSearchResults(true)}
                  className="w-full h-9 pl-10 pr-8 rounded-full bg-white/95 text-gray-900 placeholder:text-gray-500 border border-white/20 focus:ring-2 focus:ring-yellow-400 focus:outline-none text-sm shadow-sm"
                />
                {searchQuery && <button onClick={() => { setSearchQuery(''); setShowSearchResults(false); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><X className="w-3.5 h-3.5" /></button>}
                {showSearchResults && searchQuery.length >= 2 && (
                  <div className="absolute top-full mt-1.5 left-0 right-0 bg-white rounded-xl shadow-2xl border z-50 overflow-hidden max-h-80 overflow-y-auto">
                    {searchMatches.length === 0 ? (
                      <div className="p-4 text-center text-gray-400 text-sm">No matches found</div>
                    ) : searchMatches.map(m => (
                      <button key={m.id} onClick={() => { navigate(`/match/${m.id}`); setSearchQuery(''); setShowSearchResults(false); }} className="w-full px-4 py-2.5 text-left hover:bg-gray-50 border-b last:border-0 flex items-center gap-3">
                        <span className="text-base">🏏</span>
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-800 text-sm truncate">{m.name || `${m.home_team} v ${m.away_team}`}</p>
                          <p className="text-[11px] text-gray-400 truncate">{m.competition}{m.inPlay && <span className="ml-1.5 text-red-500 font-bold">LIVE</span>}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Spacer — pushes right items to the end */}
            <div className="flex-1 sm:hidden" />

            {/* Mobile search icon removed */}

            {/* Right: Login / Sign Up or Balance */}
            <div className="flex items-center gap-2 shrink-0">
              {user ? (
                <div className="flex items-center gap-2">
                  {/* Notification Bell — desktop only */}
                  <div ref={notifRef} className="relative hidden sm:block">
                    <button onClick={() => { setNotifOpen(!notifOpen); if (!notifOpen && notifUnread > 0) markNotifsRead(); }} className="p-1.5 text-white/80 hover:text-white relative">
                      <Bell className="w-5 h-5" />
                      {notifUnread > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center animate-pulse">{notifUnread > 9 ? '9+' : notifUnread}</span>}
                    </button>
                    {notifOpen && (
                      <div className="absolute right-0 top-9 w-80 bg-white rounded-xl shadow-2xl border z-50 overflow-hidden">
                        <div className="p-3 border-b bg-gray-50 flex justify-between items-center">
                          <span className="font-bold text-gray-800 text-sm">Notifications</span>
                          {notifUnread > 0 && <span className="text-[10px] bg-red-500 text-white px-2 py-0.5 rounded-full">{notifUnread} new</span>}
                        </div>
                        <div className="max-h-72 overflow-y-auto">
                          {notifList.length === 0 ? (
                            <div className="p-6 text-center text-gray-400 text-sm">No notifications</div>
                          ) : notifList.map(n => (
                            <div key={n.id} className={`p-3 border-b hover:bg-gray-50 ${!n.is_read ? 'bg-blue-50/40' : ''}`}>
                              <div className="flex items-start gap-2">
                                <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{background: n.priority === 'urgent' ? '#ef4444' : n.priority === 'important' ? '#f59e0b' : '#3b82f6'}} />
                                <div>
                                  <p className="font-semibold text-xs text-gray-800">{n.title}</p>
                                  <p className="text-[11px] text-gray-600 mt-0.5">{n.message}</p>
                                  <p className="text-[10px] text-gray-400 mt-1">{(() => { try { const d = new Date((n.created_at||'').replace(' ','T')+'Z'); return d.toLocaleString('en-IN',{timeZone:'Asia/Kolkata',day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'}); } catch { return ''; } })()}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Desktop: static balance display (no click action) */}
                  <div className="hidden sm:flex items-center gap-2 bg-white text-gray-900 rounded-lg px-3 py-1.5 shadow">
                    <div className="flex items-center gap-2 text-sm font-medium whitespace-nowrap">
                      <span>₹{Number(balance?.available_credit ?? balance?.main_balance ?? user.available_credit ?? user.balance ?? 0).toLocaleString('en-IN')}</span>
                      <span className="text-gray-300">|</span>
                      <span className="text-red-500">Exp: {fmtExposure(balance?.exposure ?? balance?.net_exposure ?? 0)}</span>
                    </div>
                  </div>
                  {/* Mobile: Deposit + Balance (search removed) */}
                  <div className="sm:hidden flex items-center gap-1 min-w-0 flex-shrink">
                    <button onClick={() => navigate('/deposit')} className="bg-orange-500 hover:bg-orange-600 text-white px-2.5 py-1 rounded-md text-[11px] font-bold whitespace-nowrap">
                      Deposit
                    </button>
                    <button
                      onClick={() => { fetchUserBalance(); fetchBestCaseWinnings(); setSheetOpen(true); }}
                      disabled={balanceLoading}
                      className="bg-white rounded-lg px-2.5 py-1 shadow-sm min-w-0 flex-shrink"
                    >
                      {balanceLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin text-green-700" />
                      ) : (
                        <span className="font-bold text-green-800 block truncate max-w-[140px]" style={{ fontSize: '12px' }}>
                          ₹{Number(balance?.available_credit ?? user.available_credit ?? user.balance ?? 0).toLocaleString('en-IN')}
                        </span>
                      )}
                    </button>
                  </div>
                  <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                    <SheetTrigger asChild><span className="hidden" /></SheetTrigger>
                    <SheetContent side="right" className="bg-white w-[75%] sm:w-[70%] max-w-none p-0 flex flex-col h-full">
                      {/* Fixed Header — Avatar + Username + X (all flex, no absolute) */}
                      <div className="bg-[#1a5c3a] text-white flex-shrink-0 px-4 py-3">
                        <div className="flex items-center w-full">
                          <div className="w-11 h-11 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0 mr-3">
                            <span className="text-white font-bold text-lg">{(user.username || user.display_name || 'U')[0].toUpperCase()}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-bold text-base truncate">{user.username || user.display_name || 'User'}</p>
                            <p className="text-green-300 text-xs">ID: {user.id || '—'}</p>
                          </div>
                          <button onClick={() => setSheetOpen(false)} className="ml-2 flex-shrink-0 text-white bg-green-700 hover:bg-green-600 rounded-full w-8 h-8 flex items-center justify-center">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Scrollable Content */}
                      <div className="flex-1 overflow-y-auto">
                        {/* Balance error banner removed — errors logged to console only */}
                        
                        {/* Balance Information */}
                        <div className="p-3">
                          <div className="bg-gray-50 rounded-xl p-4">
                            <h3 className="text-gray-400 text-[10px] font-semibold mb-2.5 uppercase tracking-wider">Balance Information</h3>
                            <div className="flex justify-between items-center py-2 border-b border-gray-100">
                              <span className="text-gray-600 text-sm">💰 Balance</span>
                              <span className="text-gray-800 font-bold text-sm">₹{Number(balance?.main_balance ?? user.balance ?? 0).toLocaleString('en-IN')}</span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b border-gray-100">
                              <span className="text-gray-600 text-sm">✅ Available</span>
                              <span className="text-green-600 font-bold text-sm">₹{Number(balance?.available_credit ?? user.available_credit ?? 0).toLocaleString('en-IN')}</span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b border-gray-100">
                              <span className="text-gray-600 text-sm">⚠️ Exposure</span>
                              <span className="text-red-500 font-bold text-sm">{fmtExposure(balance?.exposure ?? balance?.net_exposure ?? 0)}</span>
                            </div>
                            <div className="flex justify-between items-center py-2">
                              <span className="text-gray-600 text-sm">🏆 Potential Win</span>
                              {bestCaseWinnings > 0 ? (
                                <span className="text-green-600 font-bold text-sm">+₹{Number(bestCaseWinnings).toLocaleString('en-IN')}</span>
                              ) : (
                                <span className="text-gray-400 text-sm">No open bets</span>
                              )}
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 mt-3">
                            <Button onClick={() => { navigate('/deposit'); setSheetOpen(false); }} className="bg-green-700 hover:bg-green-800 h-10 text-white font-semibold text-sm">
                              💰 Deposit
                            </Button>
                            <Button onClick={() => { navigate('/withdraw'); setSheetOpen(false); }} className="bg-red-600 hover:bg-red-700 h-10 text-white font-semibold text-sm">
                              💸 Withdraw
                            </Button>
                          </div>
                        </div>
                        
                        {/* Menu Items */}
                        <div className="divide-y">
                          <SheetMenuItem icon={<UserIcon className='w-5 h-5' />} label="Profile" to="/profile" onNavigate={handleNavigate} />
                          <SheetMenuItem icon={<ReceiptText className='w-5 h-5' />} label="Open Bets" to="/open-bets" onNavigate={handleNavigate} />
                          <SheetMenuItem icon={<FileText className='w-5 h-5' />} label="Bet-History" to="/bet-history" onNavigate={handleNavigate} />
                          <SheetMenuItem icon={<Wallet className='w-5 h-5' />} label="Betting Profit & Loss" to="/win-loss" onNavigate={handleNavigate} />
                          <SheetMenuItem icon={<ReceiptText className='w-5 h-5' />} label="Account Statement" to="/account-statement" onNavigate={handleNavigate} />
                          <SheetMenuItem icon={<ShieldCheck className='w-5 h-5' />} label="Change Password" onClick={() => { setShowChangePwd(true); setSheetOpen(false); }} />
                          <SheetMenuItem icon={<Bell className='w-5 h-5' />} label={<span className="flex items-center gap-2">Notifications{notifUnread > 0 && <span className="bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 leading-none">{notifUnread}</span>}</span>} to="/notifications" onNavigate={() => { markNotifsRead(); handleNavigate(); }} />
                          <SheetMenuItem icon={<FileText className='w-5 h-5' />} label="Rules & Regulations" to="/rules" onNavigate={handleNavigate} />
                          <SheetMenuItem icon={<Settings className='w-5 h-5' />} label="Settings" to="/settings" onNavigate={handleNavigate} />
                        </div>
                      </div>
                      
                      {/* Fixed Footer */}
                      <div className="p-4 border-t bg-gray-50 flex-shrink-0">
                        <Button onClick={() => { handleLogout(); setSheetOpen(false); }} className="w-full bg-red-600 hover:bg-red-700 h-11 text-white font-semibold">
                          <LogOut className="w-4 h-4 mr-2" /> Sign Out
                        </Button>
                      </div>
                    </SheetContent>
                  </Sheet>
                  {/* Desktop: My Account dropdown + Log Out — hidden on mobile */}
                  <div ref={accountMenuRef} className="hidden sm:block relative">
                    <button onClick={() => setAccountMenuOpen(!accountMenuOpen)}
                      className="inline-flex items-center bg-transparent border border-white/60 text-white hover:bg-white hover:text-gray-900 font-medium rounded-md px-4 py-1.5 text-sm transition-colors">
                      My Account
                    </button>
                    {accountMenuOpen && (
                      <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                        {[
                          { icon: '👤', label: 'My Profile', path: '/profile' },
                          { icon: '📋', label: 'Open Bets', path: '/open-bets' },
                          { icon: '📄', label: 'Bet History', path: '/bet-history' },
                          { icon: '📊', label: 'Profit & Loss', path: '/win-loss' },
                          { icon: '📋', label: 'Account Statement', path: '/account-statement' },
                          { icon: '🔒', label: 'Change Password', path: '/settings' },
                          { icon: '🔔', label: 'Notifications', path: '/notifications' },
                          { icon: '📜', label: 'Rules & Regs', path: '/rules' },
                          { icon: '⚙️', label: 'Settings', path: '/settings' },
                        ].map(item => (
                          <button key={item.path + item.label} onClick={() => { setAccountMenuOpen(false); navigate(item.path); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors">
                            <span>{item.icon}</span> {item.label}
                          </button>
                        ))}
                        <div className="border-t border-gray-200 my-1" />
                        <button onClick={() => { setAccountMenuOpen(false); navigate('/deposit'); }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-green-600 font-semibold hover:bg-green-50 transition-colors">
                          <span>💰</span> Deposit
                        </button>
                        <button onClick={() => { setAccountMenuOpen(false); navigate('/withdraw'); }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 font-semibold hover:bg-red-50 transition-colors">
                          <span>📤</span> Withdraw
                        </button>
                      </div>
                    )}
                  </div>
                  <button onClick={() => { handleLogout(); }}
                    className="hidden sm:inline-flex items-center bg-red-500 hover:bg-red-600 text-white font-medium rounded-md px-4 py-1.5 text-sm transition-colors">
                    Log Out
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 sm:gap-3">
                  <Button
                    onClick={() => setShowLogin(true)}
                    className="bg-yellow-400 hover:bg-yellow-300 text-gray-900 font-bold rounded-md shadow-md px-2.5 py-1 text-[11px] sm:px-6 sm:py-2 sm:text-sm transition-colors"
                  >
                    Login
                  </Button>
                  <Link to="/register">
                    <Button className="bg-transparent border border-white text-white hover:bg-white hover:text-gray-900 font-bold rounded-md px-2.5 py-1 text-[11px] sm:border-2 sm:px-6 sm:py-2 sm:text-sm transition-colors">
                      Sign Up
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>

        </div>

        {/* Mobile search slide-down with results */}
        {mobileSearchOpen && (
          <div className="sm:hidden border-t border-white/10 bg-lotus-dark relative">
            <div className="px-3 py-2 flex items-center gap-2">
              <Search className="w-4 h-4 text-white/50 shrink-0" />
              <input type="text" placeholder="Search matches..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} autoFocus className="flex-1 h-8 px-3 rounded-md bg-white/95 text-gray-900 placeholder:text-gray-500 border-0 focus:ring-2 focus:ring-yellow-400 focus:outline-none text-sm" />
              <button onClick={() => { setMobileSearchOpen(false); setSearchQuery(''); setShowSearchResults(false); }} className="text-white/70 hover:text-white p-1"><X className="w-4 h-4" /></button>
            </div>
            {showSearchResults && searchQuery.length >= 2 && (
              <div className="mx-3 mb-2 bg-white rounded-xl overflow-hidden shadow-xl max-h-64 overflow-y-auto relative z-[9999]">
                {searchMatches.length === 0 ? (
                  <div className="p-4 text-center text-gray-400 text-sm">No matches found</div>
                ) : searchMatches.map(m => (
                  <button key={m.id} onMouseDown={(e) => { e.preventDefault(); navigate(`/match/${m.id}`); setSearchQuery(''); setMobileSearchOpen(false); setShowSearchResults(false); }} className="w-full px-4 py-2.5 text-left hover:bg-gray-50 active:bg-gray-100 border-b last:border-0 flex items-center gap-3">
                    <span>🏏</span>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-800 text-sm truncate">{m.name || `${m.home_team} v ${m.away_team}`}</p>
                      <p className="text-[11px] text-gray-400 truncate">{m.competition}{m.inPlay && <span className="ml-1.5 text-red-500 font-bold">LIVE</span>}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Row 2: Full-width nav menu (Home + sidebar items), edge-to-edge */}
        <nav className="w-full border-t border-white/10 flex items-center overflow-x-auto min-h-[44px] px-4 sm:px-6">
          <div className="flex items-center gap-0">
            <Link
              to="/"
              className={`flex-shrink-0 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors ${
                location.pathname === '/' ? 'text-yellow-400 bg-white/10' : 'hover:bg-white/10 text-white'
              }`}
            >
              Home
            </Link>
            {mockSportsMenu.map((item) => {
              const path = HEADER_NAV_PATH_MAP[item.name];
              const isActive = path && location.pathname === path;
              // Skip plain link for Casino — it gets a highlighted button below
              if (item.name === 'Casino') return (
                <React.Fragment key={item.id}>
                  <Link
                    to="/casino"
                    className="flex-shrink-0 py-1.5 px-3 text-sm font-bold whitespace-nowrap mx-1"
                    style={{
                      background: location.pathname === '/casino' ? '#9333ea' : 'linear-gradient(135deg, #9333ea, #c026d3)',
                      color: '#fff', borderRadius: 6, fontSize: 13,
                      boxShadow: location.pathname === '/casino' ? '0 0 10px rgba(147,51,234,0.5)' : 'none',
                    }}
                  >
                    🎰 Casino
                  </Link>
                </React.Fragment>
              );
              return (
                <React.Fragment key={item.id}>
                  <Link
                    to={path || '/'}
                    className={`flex-shrink-0 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors ${
                      isActive ? 'text-yellow-400 bg-white/10' : 'hover:bg-white/10 text-white'
                    }`}
                  >
                    {item.name}
                  </Link>
                  {item.name === 'Cricket' && (
                    <Link
                      to="/ipl-2026"
                      className="flex-shrink-0 py-1.5 px-3 text-sm font-bold whitespace-nowrap mx-1"
                      style={{
                        background: location.pathname === '/ipl-2026' ? '#ff6b00' : 'linear-gradient(135deg, #ff6b00, #ff8f00)',
                        color: '#fff', borderRadius: 6, fontSize: 13,
                        boxShadow: location.pathname === '/ipl-2026' ? '0 0 10px rgba(255,107,0,0.5)' : 'none',
                      }}
                    >
                      🏆 IPL 2026
                    </Link>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </nav>
      </header>

      {/* Scrolling ticker (public, always visible if enabled) */}
      {ticker && (
        <div style={{
          background: '#1e8067',
          color: 'white',
          padding: '6px 0',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          width: '100%',
          fontSize: '13px',
          fontWeight: 600,
        }}>
          <div style={{
            display: 'inline-block',
            animation: 'ticker-scroll 30s linear infinite',
            paddingLeft: '100%',
          }}>
            📢 {ticker}
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      <Dialog open={showChangePwd} onOpenChange={(open) => {
        setShowChangePwd(open);
        if (!open) {
          resetPasswordForm();
        }
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSavePassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Current Password</label>
              <div className="relative">
                <Input
                  type={show1 ? 'text' : 'password'}
                  placeholder="Enter current password"
                  value={pwd.current}
                  onChange={(e) => {
                    setPwd({ ...pwd, current: e.target.value });
                    if (pwdErrors.current) {
                      setPwdErrors({ ...pwdErrors, current: '' });
                    }
                  }}
                  className={`h-12 pr-10 ${pwdErrors.current ? 'border-red-500' : ''}`}
                  required
                />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600" onClick={() => setShow1(s=>!s)} aria-label="toggle">
                  {show1 ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {pwdErrors.current && (
                <p className="text-red-600 text-sm mt-1">{pwdErrors.current}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">New Password</label>
              <div className="relative">
                <Input
                  type={show2 ? 'text' : 'password'}
                  placeholder="Enter new password"
                  value={pwd.next}
                  onChange={(e) => {
                    setPwd({ ...pwd, next: e.target.value });
                    if (pwdErrors.next) {
                      setPwdErrors({ ...pwdErrors, next: '' });
                    }
                  }}
                  className={`h-12 pr-10 ${pwdErrors.next ? 'border-red-500' : ''}`}
                  required
                />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600" onClick={() => setShow2(s=>!s)} aria-label="toggle">
                  {show2 ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {pwdErrors.next && (
                <p className="text-red-600 text-sm mt-1">{pwdErrors.next}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Confirm New Password</label>
              <div className="relative">
                <Input
                  type={show3 ? 'text' : 'password'}
                  placeholder="Confirm new password"
                  value={pwd.confirm}
                  onChange={(e) => {
                    setPwd({ ...pwd, confirm: e.target.value });
                    if (pwdErrors.confirm) {
                      setPwdErrors({ ...pwdErrors, confirm: '' });
                    }
                  }}
                  className={`h-12 pr-10 ${pwdErrors.confirm ? 'border-red-500' : ''}`}
                  required
                />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600" onClick={() => setShow3(s=>!s)} aria-label="toggle">
                  {show3 ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {pwdErrors.confirm && (
                <p className="text-red-600 text-sm mt-1">{pwdErrors.confirm}</p>
              )}
            </div>
            
            {cpStep === 1 ? (
              <Button type="submit" className="w-full bg-[#C10930] hover:bg-[#6D081D] h-12" disabled={passwordLoading}>
                {passwordLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending OTP...</> : '📲 Send OTP on WhatsApp'}
              </Button>
            ) : (
              <div className="space-y-3">
                <div className="bg-green-50 p-3 rounded-lg text-center">
                  <p className="text-green-700 font-medium text-sm">💬 OTP sent to your WhatsApp!</p>
                </div>
                <Input type="number" placeholder="Enter 6-digit OTP" value={cpOtp} onChange={(e) => setCpOtp(e.target.value.slice(0,6))} className="h-12 text-center text-xl tracking-widest" />
                <Button type="button" onClick={handleVerifyPwdOtp} className="w-full bg-green-700 hover:bg-green-800 h-12" disabled={passwordLoading || cpOtp.length < 4}>
                  {passwordLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verifying...</> : '✅ Verify & Change Password'}
                </Button>
                <div className="text-center text-sm">{cpCountdown > 0 ? <span className="text-gray-400">Resend in {cpCountdown}s</span> : <button type="button" onClick={handleSendPwdOtp} className="text-green-600 font-semibold hover:underline">{passwordLoading ? 'Sending...' : 'Resend OTP'}</button>}</div>
                <button type="button" onClick={() => { setCpStep(1); setCpOtp(''); setPwdErrors({}); }} className="w-full text-gray-400 text-sm py-1">← Back</button>
              </div>
            )}
          </form>
        </DialogContent>
      </Dialog>

      {/* Login Modal */}
      <LoginModal 
        isOpen={showLogin} 
        onClose={() => setShowLogin(false)} 
        onLoginSuccess={handleLoginSuccess}
      />
    </>
  );
};

const SheetMenuItem = ({ icon, label, to, onClick, onNavigate }) => {
  const navigate = useNavigate();
  return (
    <button
      className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50"
      onClick={() => {
        if (onClick) { onClick(); return; }
        if (to) {
          if (onNavigate) onNavigate();
          navigate(to);
        }
      }}
    >
      <span className="flex items-center gap-3 text-gray-800 font-medium">
        {icon}
        {label}
      </span>
      <ChevronRight className="w-4 h-4 text-gray-400" />
    </button>
  );
};

export default Header;